import { locationTypes } from '@/app/shared/filters';
import type { ProductImage, SimpleItem } from '@/app/shared/types';
import { SHEET_NAMES, getAllocationsSheetRows } from '@/app/shared/sheets';
type FlatRow = Record<string, unknown>;
type AllocationsAssortmentGroup = Record<string, unknown>;
type AllocationsAssortmentGroupLocation = Record<string, unknown>;
type AllocationsComparableProduct = Record<string, unknown>;
type AllocationsConstraint = Record<string, unknown>;
type AllocationsProduct = Record<string, unknown>;
type AllocationsProductKpi = Record<string, unknown>;
type AllocationsProductLocation = Record<string, unknown>;
type AllocationsProductLocationDetails = Record<string, unknown>;
type AllocationsProductLocationDetailsPackedItem = Record<string, unknown>;
type AllocationsProductLocationDetailsSingleItem = Record<string, unknown>;
type AllocationsProductLocationsList = Record<string, unknown>;
type AllocationsProductLocationsListItem = Record<string, unknown>;
type AllocationsProductSummary = Record<string, unknown>;
type AllocationsProductsListItem = Record<string, unknown>;
type AllocationsInventoryLocation = {
  id: string;
  name: string;
  account_id: string;
  at_site: number | null;
  avoid_replenishment: boolean;
  brands: string[];
  city: string;
  classifications: string[];
  created_at: number;
  deleted_at: number;
  description: string | null;
  external_id: string;
  in_transit: number | null;
  inventory_cost: number | null;
  inventory_price: number | null;
  location_type: string;
  num_of_products: number | null;
  num_of_skus: number | null;
  region: string;
  reserved: number | null;
  updated_at: number;
  custom_data: Record<string, unknown>;
};
type NormalizedRow = {
  productName: string;
  productId: string;
  locationName: string;
  locationId: string;
  units: number;
  optimal: number;
  gap: number;
  atSite: number;
  similarProductsSalesRate: number | null;
  expectedCoverage: number | null;
  expectedUnitsInStore: number | null;
  coverageGap: number | null;
  whAfterAllocation: number | null;
  style: string | null;
  color: string | null;
  department: string | null;
  assortmentGroup: string | null;
  sourceLocationName: string | null;
  sourceLocationId: string | null;
  atWh: number | null;
  inTransitToWh: number | null;
  allocationDate: string | null;
  lastModified: string | null;
  modifiedBy: string | null;
  constraintsRaw: string | null;
  comparableProducts: string[];
  storeAppearanceCount: {
    in: number;
    of: number;
  } | null;
  imageUrl: string | null;
};
const SIZE_GRID = ['XS', 'S', 'M', 'L', 'XL'] as const;
const SIZE_WEIGHTS = [0.1, 0.2, 0.3, 0.25, 0.15];
const SEASON_OPTIONS = ['Spring', 'Summer', 'Fall', 'Winter'];
const BRAND_OPTIONS = ['Aurora', 'Canyon', 'Harbor', 'Atlas', 'Summit'];
const CLASSIFICATION_OPTIONS = ['A', 'B', 'C', 'D'];
const CITY_OPTIONS = [
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  'Miami',
  'Seattle',
  'Denver',
  'Atlanta',
  'Boston',
  'San Francisco',
];
const REGION_OPTIONS = ['Northeast', 'West', 'Midwest', 'South', 'Southwest', 'Northwest'];
let loggedImageSamples = 0;
const sizeOverrides = new Map<string, Record<string, number>>();
const locationSizeOverrides = new Map<string, number>();
function toStringValue(value: unknown) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}
function getImageField(row: FlatRow) {
  const candidates = ['Image', 'Image URL', 'Image Url', 'Image Link', 'Image link', 'Image_URL'];
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const value = row[key];
      if (value != null && toStringValue(value)) {
        return value;
      }
    }
  }
  const keyMatch = Object.keys(row).find((key) => {
    const normalized = key.trim().toLowerCase();
    return (
      normalized === 'image' ||
      normalized === 'image url' ||
      normalized === 'image_url' ||
      normalized === 'image link' ||
      normalized === 'image_link'
    );
  });
  return keyMatch ? row[keyMatch] : null;
}
function parseNumber(value: unknown, fallback = 0) {
  const parsed = Number(toStringValue(value));
  return Number.isFinite(parsed) ? parsed : fallback;
}
function parseOptionalNumber(value: unknown) {
  const parsed = Number(toStringValue(value));
  return Number.isFinite(parsed) ? parsed : null;
}
function parseList(value: unknown) {
  const text = toStringValue(value);
  if (!text) {
    return [];
  }
  return text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
function toSimpleItems(values: string[]): SimpleItem[] {
  return Array.from(new Set(values.filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({
      id: value,
      name: value,
      external_id: value,
    }));
}
function parseStoreAppearance(value: unknown) {
  const text = toStringValue(value);
  if (!text || !text.includes('/')) {
    return null;
  }
  const [inValue, ofValue] = text.split('/');
  const inCount = parseNumber(inValue, 0);
  const ofCount = parseNumber(ofValue, 0);
  return { in: inCount, of: ofCount };
}
function normalizeDate(value: unknown) {
  const text = toStringValue(value);
  if (!text) {
    return null;
  }
  if (text.includes('/')) {
    const [month, day, year] = text.split('/').map((part) => part.padStart(2, '0'));
    if (!year) {
      return null;
    }
    return `${year}-${month}-${day}`;
  }
  return text;
}
function normalizeDateTime(value: unknown) {
  const date = normalizeDate(value);
  if (!date) {
    return null;
  }
  return `${date}T00:00:00.000Z`;
}
function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}
function getCityRegion(id: string) {
  const hash = hashString(id);
  return {
    city: CITY_OPTIONS[hash % CITY_OPTIONS.length],
    region: REGION_OPTIONS[hash % REGION_OPTIONS.length],
  };
}
function getDerivedSeason(id: string) {
  return SEASON_OPTIONS[hashString(id) % SEASON_OPTIONS.length];
}
function getDerivedBrand(id: string) {
  return BRAND_OPTIONS[hashString(id) % BRAND_OPTIONS.length];
}
function getDerivedClassification(id: string) {
  return CLASSIFICATION_OPTIONS[hashString(id) % CLASSIFICATION_OPTIONS.length];
}
function getDerivedLocationType(id: string) {
  return locationTypes[hashString(id) % locationTypes.length] ?? 'store';
}
function randomInRange(min: number, max: number, seed: number) {
  const normalized = (seed % 1000) / 1000;
  return min + normalized * (max - min);
}
function deterministicExpectedCoverage(seedKey: string) {
  return Math.round(randomInRange(14, 40, hashString(seedKey)));
}
function deterministicSalesRate(seedKey: string) {
  const value = randomInRange(0.9, 3.95, hashString(seedKey));
  return Math.round(value * 100) / 100;
}
function buildProductLocationId(row: NormalizedRow) {
  return `${row.productId}:${row.locationId}`;
}
function normalizeSizeKey(value: string) {
  return value.trim().toUpperCase();
}
function normalizeSizeOverrides(quantities: Record<string, number | string | null | undefined>) {
  const normalized: Record<string, number> = {};
  Object.entries(quantities).forEach(([key, val]) => {
    const normalizedKey = normalizeSizeKey(key);
    if (!SIZE_GRID.includes(normalizedKey as (typeof SIZE_GRID)[number])) {
      return;
    }
    if (val === null || val === undefined || val === '') {
      return;
    }
    const parsed = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(parsed)) {
      return;
    }
    normalized[normalizedKey] = Math.max(0, parsed);
  });
  return normalized;
}
function getSizeOverride(id: string) {
  return sizeOverrides.get(id) ?? null;
}
function sumOverride(override: Record<string, number>) {
  return Object.values(override).reduce((sum, value) => sum + value, 0);
}
function getSizeDistribution(id: string, baseTotal: number) {
  const override = getSizeOverride(id);
  if (override) {
    const baseDistribution = distributeUnits(baseTotal, id);
    return SIZE_GRID.map((size) => ({
      size,
      quantity: Object.prototype.hasOwnProperty.call(override, size)
        ? (override[size] ?? 0)
        : (baseDistribution.find((item) => item.size === size)?.quantity ?? 0),
    }));
  }
  return distributeUnits(baseTotal, id);
}
export function applyAllocationsSizeOverrides(ids: string[], quantities: Record<string, number>) {
  const normalized = normalizeSizeOverrides(quantities);
  ids.forEach((id) => {
    const current = sizeOverrides.get(id) ?? {};
    sizeOverrides.set(id, { ...current, ...normalized });
  });
}
export function applyAllocationsSkuOverrides(
  items: {
    id: string;
    user_quantity: number | null;
  }[],
) {
  items.forEach((item) => {
    if (item.user_quantity === null || item.user_quantity === undefined) {
      return;
    }
    const value = Number(item.user_quantity);
    if (!Number.isFinite(value)) {
      return;
    }
    const [productLocationId, size] = item.id.split(':single:');
    if (!productLocationId || !size) {
      return;
    }
    locationSizeOverrides.set(`${productLocationId}:${normalizeSizeKey(size)}`, Math.max(0, value));
  });
}
function getSkuOverride(key: string) {
  return locationSizeOverrides.get(key);
}
export function clearAllocationsSizeOverrides(ids?: string[]) {
  if (!ids) {
    sizeOverrides.clear();
    locationSizeOverrides.clear();
    return;
  }
  ids.forEach((id) => sizeOverrides.delete(id));
}
function normalizeImageUrl(url: string): string {
  const trimmed = url.trim();
  const imageFormula = trimmed.match(/^=IMAGE\((.+)\)$/i);
  if (imageFormula) {
    const rawArgs = imageFormula[1] ?? '';
    const firstArg = rawArgs.split(',')[0] ?? '';
    const stripped = firstArg.trim().replace(/^"|"$/g, '');
    return normalizeImageUrl(stripped);
  }
  if (!trimmed.includes('drive.google.com') && !trimmed.includes('googleusercontent.com')) {
    return trimmed;
  }
  const idMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  const idFromPath = idMatch ? idMatch[1] : null;
  if (idFromPath) {
    return `https://lh3.googleusercontent.com/d/${idFromPath}`;
  }
  try {
    const parsed = new URL(trimmed);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return `https://lh3.googleusercontent.com/d/${idFromQuery}`;
    }
  } catch {}
  return trimmed;
}
function extractDriveId(url: string) {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match?.[1]) {
    return match[1];
  }
  try {
    const parsed = new URL(url);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return idFromQuery;
    }
  } catch {}
  return null;
}
function buildImage(url: string | null): ProductImage[] {
  if (!url) {
    return [];
  }
  const normalized = normalizeImageUrl(url);
  const domainMatch = normalized.match(/^https?:\/\/([^/]+)/);
  const domain = domainMatch ? domainMatch[1] : '';
  const images: ProductImage[] = [
    {
      public: normalized,
      thumb: normalized,
      domain,
    },
  ];
  const driveId = extractDriveId(url) ?? extractDriveId(normalized);
  if (driveId) {
    const fallback = `https://drive.google.com/uc?export=view&id=${driveId}`;
    if (fallback !== normalized) {
      images.push({
        public: fallback,
        thumb: fallback,
        domain: 'drive.google.com',
      });
    }
  }
  if (loggedImageSamples < 3 && typeof window !== 'undefined' && (import.meta as any)?.env?.DEV) {
    loggedImageSamples += 1;
    console.info('[demo images][allocations]', {
      raw: url,
      normalized,
      images,
    });
  }
  return images;
}
function parseConstraints(raw: string | null): AllocationsConstraint[] {
  if (!raw) {
    return [];
  }
  const value = raw.toLowerCase();
  const result: AllocationsConstraint[] = [];
  if (value.includes('full set')) {
    result.push({ name: 'full_set_required', value: true });
  }
  const minSizesMatch = value.match(/min sizes?:?\s*(\d+)/);
  if (minSizesMatch) {
    result.push({ name: 'min_sizes_number', value: Number(minSizesMatch[1]) });
  }
  const maxSizesMatch = value.match(/max sizes?:?\s*(\d+)/);
  if (maxSizesMatch) {
    result.push({
      name: 'max_product_quantity',
      value: Number(maxSizesMatch[1]),
    });
  }
  if (value.includes('wh capacity')) {
    result.push({ name: 'insufficient_wh', value: true });
  }
  return result;
}
function rotateArray<T>(items: T[], shift: number) {
  if (!items.length) {
    return items;
  }
  const offset = ((shift % items.length) + items.length) % items.length;
  return items.slice(offset).concat(items.slice(0, offset));
}
function distributeUnits(total: number, seedKey: string) {
  const seed = hashString(seedKey);
  const rotatedSizes = rotateArray([...SIZE_GRID], seed);
  const rotatedWeights = rotateArray([...SIZE_WEIGHTS], seed);
  const raw = rotatedWeights.map((weight) => Math.max(0, Math.round(total * weight)));
  let remainder = total - raw.reduce((sum, value) => sum + value, 0);
  for (let i = 0; remainder > 0 && i < raw.length; i += 1) {
    raw[i] += 1;
    remainder -= 1;
  }
  for (let i = 0; remainder < 0; i += 1) {
    const index = i % raw.length;
    if (raw[index] > 0) {
      raw[index] -= 1;
      remainder += 1;
    }
    if (i > raw.length * 4) {
      break;
    }
  }
  return rotatedSizes.map((size, index) => ({
    size,
    quantity: raw[index] ?? 0,
  }));
}
async function loadFlatRows(): Promise<NormalizedRow[]> {
  const rows = await getAllocationsSheetRows<FlatRow>(SHEET_NAMES.allocationsFlat);
  return rows.map((row) => ({
    productName: toStringValue(row['Product']),
    productId: toStringValue(row['Product ID']),
    locationName: toStringValue(row['Location']),
    locationId: toStringValue(row['Location ID']) || toStringValue(row['Location']),
    units: parseNumber(row['Units'], 0),
    optimal: parseNumber(row['Optimal'], 0),
    gap: parseNumber(row['Gap'], 0),
    atSite: parseNumber(row['at site'], 0),
    similarProductsSalesRate: parseOptionalNumber(row['Similar products sales rate']),
    expectedCoverage: parseOptionalNumber(row['Expected coverage']),
    expectedUnitsInStore: parseOptionalNumber(row['Expected units in stores']),
    coverageGap: parseOptionalNumber(row['Coverage gap']),
    whAfterAllocation: parseOptionalNumber(row['WH after allocation']),
    style: toStringValue(row['Style']) || null,
    color: toStringValue(row['Color']) || null,
    department: toStringValue(row['Department']) || null,
    assortmentGroup: toStringValue(row['Assortment Group']) || null,
    sourceLocationName: toStringValue(row['Source Location']) || null,
    sourceLocationId:
      toStringValue(row['Source Location ID']) ||
      toStringValue(row['Source Location']) ||
      toStringValue(row['Location ID']) ||
      toStringValue(row['Location']) ||
      null,
    atWh: parseOptionalNumber(row['At WH']),
    inTransitToWh: parseOptionalNumber(row['In transit to WH']),
    allocationDate: normalizeDate(row['Allocation date']),
    lastModified: normalizeDateTime(row['Last modified']),
    modifiedBy: toStringValue(row['Modified by']) || null,
    constraintsRaw: toStringValue(row['Constraints']) || null,
    comparableProducts: parseList(row['Comparable products']),
    storeAppearanceCount: parseStoreAppearance(row['Store appearance count']),
    imageUrl: toStringValue(getImageField(row)) || null,
  }));
}
export async function getAllocationsProductLocationsFromFlat() {
  const rows = await loadFlatRows();
  return rows.map((row) => {
    const seedKey = `${row.productId}:${row.locationId}`;
    const id = buildProductLocationId(row);
    const override = getSizeOverride(id);
    const baseDistribution = distributeUnits(row.units, id);
    const mixedDistribution = override
      ? SIZE_GRID.map((size) => ({
          size,
          quantity: Object.prototype.hasOwnProperty.call(override, size)
            ? (override[size] ?? 0)
            : (baseDistribution.find((item) => item.size === size)?.quantity ?? 0),
        }))
      : baseDistribution;
    const recommendedUnits = mixedDistribution.reduce((sum, item) => sum + item.quantity, 0);
    const gap = override ? Math.max(0, row.optimal - recommendedUnits) : row.gap;
    const whAfterAllocation = override
      ? Math.max(0, (row.atWh ?? 0) - recommendedUnits)
      : row.whAfterAllocation;
    return {
      id,
      location_id: row.locationId,
      location_external_id: row.locationId,
      location_name: row.locationName,
      product_id: row.productId,
      product_external_id: row.productId,
      product_name: row.productName,
      source_location_name: row.sourceLocationName,
      source_location_external_id: row.sourceLocationId,
      source_location_id: row.sourceLocationId ?? row.locationId,
      allocation_date: row.allocationDate,
      total_pack_recommended_quantity: 0,
      total_pack_optimal_quantity: 0,
      total_single_recommended_quantity: recommendedUnits,
      total_single_optimal_quantity: row.optimal,
      pack_external_ids: null,
      pack_quantity_gap: 0,
      single_quantity_gap: gap,
      single_at_wh: row.atWh,
      pack_at_wh: 0,
      at_wh: row.atWh,
      single_in_transit: row.inTransitToWh,
      pack_in_transit: 0,
      single_wh_after_allocation: whAfterAllocation,
      pack_wh_after_allocation: 0,
      edited: false,
      constraints: parseConstraints(row.constraintsRaw),
      last_modified: row.lastModified,
      modified_by: row.modifiedBy,
      department_name: row.department,
      assortment_group_name: row.assortmentGroup,
      style_names: row.style ? [row.style] : [],
      color_names: row.color ? [row.color] : [],
      similar_product_sales_rate: deterministicSalesRate(seedKey),
      expected_coverage: deterministicExpectedCoverage(seedKey),
      coverage_gap: row.coverageGap ?? null,
      ag_location_expected_coverage: deterministicExpectedCoverage(seedKey),
      ag_location_coverage_gap: row.coverageGap ?? null,
      category_names: row.department ? [row.department] : [],
      at_site: row.atSite,
      in_transit: 0,
      expected_units_in_store: row.expectedUnitsInStore,
      demo_base_units: row.units,
    } satisfies AllocationsProductLocation;
  });
}
export async function getAllocationsProductsFromFlat(): Promise<AllocationsProduct[]> {
  const rows = await loadFlatRows();
  const byProduct = new Map<string, NormalizedRow[]>();
  rows.forEach((row) => {
    if (!row.productId) {
      return;
    }
    if (!byProduct.has(row.productId)) {
      byProduct.set(row.productId, []);
    }
    byProduct.get(row.productId)!.push(row);
  });
  return Array.from(byProduct.entries()).map(([productId, productRows]) => {
    const first = productRows[0];
    const sourceRow =
      productRows.find((row) => row.sourceLocationId || row.sourceLocationName) ?? first;
    const seedKey = productId;
    return {
      id: productId,
      product_id: productId,
      product_external_id: productId,
      product_name: first.productName,
      source_location_name: sourceRow.sourceLocationName ?? sourceRow.locationName ?? '',
      source_location_external_id: sourceRow.sourceLocationId ?? sourceRow.locationId ?? '',
      at_wh: productRows.reduce((sum, item) => sum + (item.atWh ?? 0), 0),
      in_transit_to_wh: productRows.reduce((sum, item) => sum + (item.inTransitToWh ?? 0), 0),
      pack_ids: [],
      similar_products_sales_rate: deterministicSalesRate(seedKey),
      comparable_products: first.comparableProducts,
    } satisfies AllocationsProduct;
  });
}
export async function getAllocationsAssortmentGroupsFromFlat(): Promise<
  AllocationsAssortmentGroup[]
> {
  const rows = await loadFlatRows();
  const byGroup = new Map<string, NormalizedRow[]>();
  rows.forEach((row) => {
    const group = row.assortmentGroup ?? 'Unknown';
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
    }
    byGroup.get(group)!.push(row);
  });
  return Array.from(byGroup.entries()).map(([name, groupRows]) => {
    const sourceLocationIds = Array.from(
      new Set(groupRows.map((row) => row.sourceLocationId).filter(Boolean) as string[]),
    );
    return {
      assortment_group_id: name.toLowerCase().replace(/\s+/g, '-'),
      assortment_group_name: name,
      source_location_ids: sourceLocationIds,
      source_location_external_ids: sourceLocationIds,
      departments: Array.from(
        new Set(groupRows.map((row) => row.department).filter(Boolean) as string[]),
      ),
      categories: Array.from(
        new Set(groupRows.map((row) => row.department).filter(Boolean) as string[]),
      ),
      at_wh: groupRows.reduce((sum, row) => sum + (row.atWh ?? 0), 0),
      in_transit_to_wh: groupRows.reduce((sum, row) => sum + (row.inTransitToWh ?? 0), 0),
    } satisfies AllocationsAssortmentGroup;
  });
}
export async function getAllocationsAssortmentGroupLocationsFromFlat(): Promise<
  AllocationsAssortmentGroupLocation[]
> {
  const rows = await loadFlatRows();
  const groups = new Map<string, NormalizedRow[]>();
  rows.forEach((row) => {
    const group = row.assortmentGroup ?? 'Unknown';
    const key = `${group}:${row.locationId}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(row);
  });
  return Array.from(groups.entries()).map(([key, groupRows]) => {
    const [groupName] = key.split(':');
    const first = groupRows[0];
    const totalUnits = groupRows.reduce((sum, row) => sum + row.units, 0);
    const totalOptimal = groupRows.reduce((sum, row) => sum + row.optimal, 0);
    const totalGap = groupRows.reduce((sum, row) => sum + row.gap, 0);
    const expectedCoverage = deterministicExpectedCoverage(key);
    const coverage = deterministicExpectedCoverage(`${key}:coverage`);
    return {
      id: key,
      assortment_group_id: groupName.toLowerCase().replace(/\s+/g, '-'),
      assortment_group_name: groupName,
      location_id: first.locationId,
      location_external_id: first.locationId,
      location_name: first.locationName,
      product_ids: groupRows.map((row) => row.productId),
      product_external_ids: groupRows.map((row) => row.productId),
      product_names: groupRows.map((row) => row.productName),
      source_location_name: first.sourceLocationName ?? '',
      source_location_external_id: first.sourceLocationId ?? '',
      source_location_id: first.sourceLocationId ?? '',
      department_names: Array.from(
        new Set(groupRows.map((row) => row.department).filter(Boolean) as string[]),
      ),
      allocation_date: first.allocationDate,
      created_at: first.lastModified ?? new Date().toISOString(),
      edited: false,
      total_pack_recommended_quantity: 0,
      total_pack_optimal_quantity: 0,
      total_single_recommended_quantity: totalUnits,
      total_single_optimal_quantity: totalOptimal,
      last_modified: first.lastModified ?? new Date().toISOString(),
      pack_quantity_gap: 0,
      single_quantity_gap: totalGap,
      pack_at_wh: 0,
      single_at_wh: groupRows.reduce((sum, row) => sum + (row.atWh ?? 0), 0),
      pack_in_transit: 0,
      single_in_transit: groupRows.reduce((sum, row) => sum + (row.inTransitToWh ?? 0), 0),
      pack_wh_after_allocation: 0,
      single_wh_after_allocation: groupRows.reduce(
        (sum, row) => sum + (row.whAfterAllocation ?? 0),
        0,
      ),
      pack_external_ids: [],
      modified_by: null,
      constraints: {
        full_set_required: false,
        max_product_quantity: null,
        min_product_quantity: null,
        min_sizes_number: null,
      },
      at_site: groupRows.reduce((sum, row) => sum + row.atSite, 0),
      current_inventory_at_pipe: 0,
      sales: 0,
      average_sales_rate: deterministicSalesRate(key),
      coverage,
      expected_coverage: expectedCoverage,
      coverage_gap: coverage - expectedCoverage,
      category_names: Array.from(
        new Set(groupRows.map((row) => row.department).filter(Boolean) as string[]),
      ),
      expected_units_in_store: groupRows.reduce(
        (sum, row) => sum + (row.expectedUnitsInStore ?? 0),
        0,
      ),
      in_transit_quantities: 0,
    } satisfies AllocationsAssortmentGroupLocation;
  });
}
export async function getAllocationsProductsListFromFlat(): Promise<AllocationsProductsListItem[]> {
  const products = await getAllocationsProductsFromFlat();
  return products.map((product) => ({
    id: product.id,
    name: product.product_name,
    external_id: product.product_external_id,
  }));
}
export async function getAllocationsProductLocationsListFromFlat(
  productId: string,
): Promise<AllocationsProductLocationsList> {
  const rows = await getAllocationsProductLocationsFromFlat();
  const filtered = rows.filter((row) => row.product_id === productId);
  const data = filtered.map((row) => {
    const sizeDistribution = getSizeDistribution(
      row.id,
      (
        row as {
          demo_base_units?: number;
        }
      ).demo_base_units ??
        row.total_single_recommended_quantity ??
        0,
    );
    return {
      at_site: row.at_site ?? 0,
      in_transit: row.in_transit ?? 0,
      location_name: row.location_name ?? '',
      location_external_id: row.location_external_id ?? '',
      allocation_product_location_id: row.id,
      edited: row.edited,
      size_grid: sizeDistribution.map((item) => {
        const overrideKey = `${row.id}:${item.size}`;
        const override = getSkuOverride(overrideKey);
        return {
          size: item.size,
          in_packs: 0,
          in_singles: override ?? item.quantity,
        };
      }),
      pack_mixture: [],
    } satisfies AllocationsProductLocationsListItem;
  });
  return {
    total_locations: data.length,
    product_location_count: data.length,
    data,
  } satisfies AllocationsProductLocationsList;
}
export async function getAllocationsProductSummaryFromFlat(
  productId: string,
): Promise<AllocationsProductSummary | null> {
  const rows = await getAllocationsProductLocationsFromFlat();
  const productRows = rows.filter((row) => row.product_id === productId);
  if (!productRows.length) {
    return null;
  }
  const flatRows = await loadFlatRows();
  const flatProduct = flatRows.find((row) => row.productId === productId);
  const sizeTotals = new Map<
    string,
    {
      in_singles: number;
      in_packs: number;
    }
  >();
  const whTotals = new Map<
    string,
    {
      in_singles: number;
      in_packs: number;
    }
  >();
  productRows.forEach((row) => {
    const distribution = getSizeDistribution(
      row.id,
      (
        row as {
          demo_base_units?: number;
        }
      ).demo_base_units ??
        row.total_single_recommended_quantity ??
        0,
    );
    const normalizedDistribution = distribution.map((item) => {
      const overrideKey = `${row.id}:${item.size}`;
      const override = getSkuOverride(overrideKey);
      return {
        size: item.size,
        quantity: override ?? item.quantity,
      };
    });
    const allocationBySize = new Map<string, number>();
    normalizedDistribution.forEach(({ size, quantity }) => {
      const current = sizeTotals.get(size) ?? { in_singles: 0, in_packs: 0 };
      current.in_singles += quantity;
      sizeTotals.set(size, current);
      allocationBySize.set(size, quantity);
    });
    const whUnits = (row.single_at_wh ?? 0) + (row.single_in_transit ?? 0);
    if (whUnits > 0) {
      const whDistribution = distributeUnits(whUnits, row.id);
      whDistribution.forEach(({ size, quantity }) => {
        const allocationUnits = allocationBySize.get(size) ?? 0;
        const leftoverUnits = Math.max(0, quantity - allocationUnits);
        if (leftoverUnits <= 0) {
          return;
        }
        const current = whTotals.get(size) ?? { in_singles: 0, in_packs: 0 };
        current.in_singles += leftoverUnits;
        whTotals.set(size, current);
      });
    }
  });
  const totals = {
    totalSingles: Array.from(sizeTotals.values()).reduce((sum, value) => {
      return sum + value.in_singles + value.in_packs;
    }, 0),
  };
  const first = productRows[0];
  const warehouseSizeCurve = SIZE_GRID.map((size) => ({
    size,
    in_packs: 0,
    in_singles: whTotals.get(size)?.in_singles ?? 0,
    allocation: sizeTotals.get(size)?.in_singles ?? 0,
  }));
  return {
    product_name: first.product_name ?? '',
    product_external_id: first.product_external_id ?? '',
    style: first.style_names?.[0] ?? '',
    color: first.color_names?.[0] ?? '',
    brand: '',
    department: first.department_name ?? '',
    total_allocation_in_packs: 0,
    total_packs: 0,
    total_allocation_in_singles: totals.totalSingles,
    total_singles: totals.totalSingles,
    size_curve: Array.from(sizeTotals.entries()).map(([size, values]) => ({
      size,
      in_packs: values.in_packs,
      in_singles: values.in_singles,
    })),
    wh_size_curve: warehouseSizeCurve,
    images: buildImage(flatProduct?.imageUrl ?? null),
  } satisfies AllocationsProductSummary;
}
export async function getAllocationsProductLocationDetailsFromFlat(
  productLocationId: string,
): Promise<AllocationsProductLocationDetails | null> {
  const rows = await getAllocationsProductLocationsFromFlat();
  const row = rows.find((item) => item.id === productLocationId);
  if (!row) {
    return null;
  }
  const flatRows = await loadFlatRows();
  const flatRow = flatRows.find(
    (item) => item.productId === row.product_id && item.locationId === row.location_id,
  );
  const distribution = getSizeDistribution(
    row.id,
    (
      row as {
        demo_base_units?: number;
      }
    ).demo_base_units ??
      row.total_single_recommended_quantity ??
      0,
  );
  const perSizeSaleRate = Math.max(
    0.1,
    (row.similar_product_sales_rate ?? deterministicSalesRate(row.id)) / SIZE_GRID.length,
  );
  const whSinglesBySize = new Map(
    distributeUnits(row.single_at_wh ?? 0, `${row.id}:wh`).map((item) => [
      item.size,
      item.quantity,
    ]),
  );
  const whInTransitBySize = new Map(
    distributeUnits(row.single_in_transit ?? 0, `${row.id}:wh-transit`).map((item) => [
      item.size,
      item.quantity,
    ]),
  );
  const whAfterAllocationBySize = new Map(
    distributeUnits(row.single_wh_after_allocation ?? 0, `${row.id}:wh-after`).map((item) => [
      item.size,
      item.quantity,
    ]),
  );
  const atSiteTotal = row.at_site ?? null;
  const atSiteBySize = new Map(
    distributeUnits(atSiteTotal ?? 0, `${row.id}:at-site`).map((item) => [
      item.size,
      item.quantity,
    ]),
  );
  const inTransitTotal = row.in_transit ?? null;
  const inTransitBySize = new Map(
    distributeUnits(inTransitTotal ?? 0, `${row.id}:in-transit`).map((item) => [
      item.size,
      item.quantity,
    ]),
  );
  const singles: AllocationsProductLocationDetailsSingleItem[] = distribution.map((item) => ({
    id: `${productLocationId}:single:${item.size}`,
    sku_id: `${row.product_id}-${item.size}`,
    sku_external_id: `${row.product_external_id}-${item.size}`,
    size_name: item.size,
    recommended_quantity: item.quantity,
    allocation_singles: getSkuOverride(`${row.id}:${item.size}`) ?? item.quantity,
    in_transit_to_wh: whInTransitBySize.get(item.size) ?? 0,
    singles_at_wh: whSinglesBySize.get(item.size) ?? 0,
    singles_wh_after_allocation: whAfterAllocationBySize.get(item.size) ?? 0,
    expected_sale_rate: perSizeSaleRate,
    at_site: atSiteTotal === null ? null : (atSiteBySize.get(item.size) ?? 0),
    in_transit: inTransitTotal === null ? null : (inTransitBySize.get(item.size) ?? 0),
  }));
  const packs: AllocationsProductLocationDetailsPackedItem[] = [];
  const sizeCurve = distribution.reduce<
    Record<
      string,
      {
        recommended: number;
        allocation: number;
      }
    >
  >((acc, item) => {
    acc[item.size] = { recommended: item.quantity, allocation: item.quantity };
    return acc;
  }, {});
  const saleRate = row.similar_product_sales_rate ?? deterministicSalesRate(row.id);
  const accountExpectedCoverage =
    saleRate > 0
      ? Math.max(
          1,
          Math.round(
            ((row.total_single_recommended_quantity ?? 0) + (row.at_site ?? 0)) / (2 * saleRate),
          ),
        )
      : null;
  return {
    images: buildImage(flatRow?.imageUrl ?? null),
    account_expected_coverage: accountExpectedCoverage,
    ag_location_level: {
      allocation_units_all: row.total_single_recommended_quantity ?? 0,
      total_inventory_sale_rate: row.similar_product_sales_rate ?? null,
      total_inventory_site_qty: row.at_site ?? null,
    },
    singles,
    packs,
    size_curve: sizeCurve,
    wh_units: {
      singles: row.single_at_wh ?? 0,
      packs: 0,
    },
    allocation_units: {
      singles: row.total_single_recommended_quantity ?? 0,
      packs: 0,
    },
    wh_units_after_allocation: row.single_wh_after_allocation ?? 0,
    product_external_id: row.product_external_id ?? '',
    product_name: row.product_name ?? '',
    location_external_id: row.location_external_id ?? '',
    location_name: row.location_name ?? '',
  } satisfies AllocationsProductLocationDetails;
}
export async function getAllocationsProductsNavigationFromFlat(productId: string): Promise<{
  previous: AllocationsProductsListItem | null;
  current: AllocationsProductsListItem;
  next: AllocationsProductsListItem | null;
} | null> {
  const list = await getAllocationsProductsListFromFlat();
  const index = list.findIndex((item) => item.id === productId);
  if (index === -1) {
    return null;
  }
  return {
    previous: list[index - 1] ?? null,
    current: list[index],
    next: list[index + 1] ?? null,
  };
}
export async function getAllocationsProductKpiFromFlat(
  productId: string,
): Promise<AllocationsProductKpi> {
  const rows = await loadFlatRows();
  const productRows = rows.filter((row) => row.productId === productId);
  const agNames = new Set(
    productRows.map((row) => row.assortmentGroup).filter(Boolean) as string[],
  );
  const agUnits = rows.reduce((sum, row) => {
    if (!row.assortmentGroup || !agNames.has(row.assortmentGroup)) {
      return sum;
    }
    return sum + row.units;
  }, 0);
  const seedKey = productId;
  const expectedCoverage = deterministicExpectedCoverage(seedKey);
  const coverageGap = deterministicExpectedCoverage(`${seedKey}:gap`) - expectedCoverage;
  return {
    ag_level: {
      allocation_units_all: agUnits,
      expected_coverage: expectedCoverage,
      coverage_gap: coverageGap,
    },
    product_level: {
      similar_products_sale_rate: deterministicSalesRate(seedKey),
      expected_coverage: expectedCoverage,
    },
  } satisfies AllocationsProductKpi;
}
export async function getAllocationsComparableProductsFromFlat(
  productId: string,
): Promise<AllocationsComparableProduct[]> {
  const rows = await loadFlatRows();
  const row = rows.find((item) => item.productId === productId);
  if (!row) {
    return [];
  }
  const comparableIds = new Set(row.comparableProducts);
  const uniqueRows = new Map<string, NormalizedRow>();
  rows.forEach((item) => {
    if (!item.productId || !comparableIds.has(item.productId)) {
      return;
    }
    if (!uniqueRows.has(item.productId)) {
      uniqueRows.set(item.productId, item);
    }
  });
  return Array.from(uniqueRows.values()).map((item) => ({
    id: item.productId,
    name: item.productName,
    external_id: item.productId,
    images: buildImage(item.imageUrl),
    season: [getDerivedSeason(item.productId)],
    categories: item.department ? [item.department] : [],
    department: item.department ? [item.department] : [],
    assortment_group: item.assortmentGroup ?? null,
    allocations_date: item.allocationDate,
    avg_sale_rate: deterministicSalesRate(item.productId),
    locations_availability: item.storeAppearanceCount ?? { in: 0, of: 0 },
  }));
}
export async function getAllocationsComparableProductsCatalogFromFlat(): Promise<
  AllocationsComparableProduct[]
> {
  const rows = await loadFlatRows();
  const uniqueRows = new Map<string, NormalizedRow>();
  rows.forEach((item) => {
    if (!item.productId) {
      return;
    }
    if (!uniqueRows.has(item.productId)) {
      uniqueRows.set(item.productId, item);
    }
  });
  return Array.from(uniqueRows.values()).map((item) => ({
    id: item.productId,
    name: item.productName,
    external_id: item.productId,
    images: buildImage(item.imageUrl),
    season: [getDerivedSeason(item.productId)],
    categories: item.department ? [item.department] : [],
    department: item.department ? [item.department] : [],
    assortment_group: item.assortmentGroup ?? null,
    allocations_date: item.allocationDate,
    avg_sale_rate: deterministicSalesRate(item.productId),
    locations_availability: item.storeAppearanceCount ?? { in: 0, of: 0 },
  }));
}
export async function getAllocationsFilterOptionsFromFlat(): Promise<{
  locations: SimpleItem[];
  products: SimpleItem[];
  departments: SimpleItem[];
  categories: SimpleItem[];
  seasons: SimpleItem[];
  assortmentGroups: SimpleItem[];
  brands: SimpleItem[];
  classifications: SimpleItem[];
  regions: SimpleItem[];
  cities: SimpleItem[];
}> {
  const rows = await loadFlatRows();
  const locationsMap = new Map<string, string>();
  const productsMap = new Map<string, string>();
  const departments = new Set<string>();
  const assortmentGroups = new Set<string>();
  const brands = new Set<string>();
  const classifications = new Set<string>();
  rows.forEach((row) => {
    if (row.locationId) {
      locationsMap.set(row.locationId, row.locationName || row.locationId);
      brands.add(getDerivedBrand(row.locationId));
      classifications.add(getDerivedClassification(row.locationId));
    }
    if (row.sourceLocationId) {
      locationsMap.set(row.sourceLocationId, row.sourceLocationName || row.sourceLocationId);
      brands.add(getDerivedBrand(row.sourceLocationId));
      classifications.add(getDerivedClassification(row.sourceLocationId));
    }
    if (row.productId) {
      productsMap.set(row.productId, row.productName || row.productId);
    }
    if (row.department) {
      departments.add(row.department);
    }
    if (row.assortmentGroup) {
      assortmentGroups.add(row.assortmentGroup);
    }
  });
  const locations = Array.from(locationsMap.entries()).map(([id, name]) => ({
    id,
    name,
    external_id: id,
  }));
  const products = Array.from(productsMap.entries()).map(([id, name]) => ({
    id,
    name,
    external_id: id,
  }));
  const departmentItems = toSimpleItems(Array.from(departments));
  const categories = departmentItems;
  const seasons = toSimpleItems(
    Array.from(new Set(rows.map((row) => getDerivedSeason(row.productId)))),
  );
  const regionSet = new Set<string>();
  const citySet = new Set<string>();
  locations.forEach((location) => {
    const { city, region } = getCityRegion(location.id);
    citySet.add(city);
    regionSet.add(region);
  });
  return {
    locations,
    products,
    departments: departmentItems,
    categories,
    seasons,
    assortmentGroups: toSimpleItems(Array.from(assortmentGroups)),
    brands: toSimpleItems(Array.from(brands)),
    classifications: toSimpleItems(Array.from(classifications)),
    regions: toSimpleItems(Array.from(regionSet)),
    cities: toSimpleItems(Array.from(citySet)),
  };
}
export async function getAllocationsInventoryLocationsFromFlat(): Promise<
  AllocationsInventoryLocation[]
> {
  const rows = await loadFlatRows();
  const locations = new Map<
    string,
    {
      id: string;
      name: string;
    }
  >();
  rows.forEach((row) => {
    if (row.locationId) {
      locations.set(row.locationId, {
        id: row.locationId,
        name: row.locationName || row.locationId,
      });
    }
    if (row.sourceLocationId) {
      locations.set(row.sourceLocationId, {
        id: row.sourceLocationId,
        name: row.sourceLocationName || row.sourceLocationId,
      });
    }
  });
  return Array.from(locations.values()).map((location) => {
    const { city, region } = getCityRegion(location.id);
    const brand = getDerivedBrand(location.id);
    const classification = getDerivedClassification(location.id);
    return {
      id: location.id,
      name: location.name,
      account_id: 'demo-account',
      at_site: null,
      avoid_replenishment: hashString(location.id) % 5 === 0,
      brands: [brand],
      city,
      classifications: [classification],
      created_at: 0,
      deleted_at: 0,
      description: null,
      external_id: location.id,
      in_transit: null,
      inventory_cost: null,
      inventory_price: null,
      location_type: getDerivedLocationType(location.id),
      num_of_products: null,
      num_of_skus: null,
      region,
      reserved: null,
      updated_at: 0,
      custom_data: {},
    } satisfies AllocationsInventoryLocation;
  });
}
export async function getAllocationsSimilarProductsFromFlat() {
  const rows = await loadFlatRows();
  const byProduct = new Map<string, NormalizedRow>();
  rows.forEach((row) => {
    if (!byProduct.has(row.productId)) {
      byProduct.set(row.productId, row);
    }
  });
  return Array.from(byProduct.values()).map((row) => ({
    id: row.productId,
    allocation_product_id: row.productId,
    external_id: row.productId,
    department_name: row.department ? [row.department] : [],
    category_name: row.department ? [row.department] : [],
    name: row.productName,
    images: buildImage(row.imageUrl),
    at_wh: { singles: row.atWh ?? 0, packs: 0 },
    in_transit_to_wh: { singles: row.inTransitToWh ?? 0, packs: 0 },
    selected_products: row.comparableProducts,
    avg_sale_rate: deterministicSalesRate(row.productId),
  }));
}
